// Function to force load lazy images
const forceLoadLazyImages = ( slide ) => {
  const mediaWrapper = slide.querySelector( '.e-div--media-wrapper' );
  if ( !mediaWrapper ) return;

  const img = mediaWrapper.querySelector( 'img' );
  if ( !img ) return;

  if ( img.loading === 'lazy' ) {
    img.loading = 'eager';
    const src = img.src;
    img.src = '';
    img.src = src;
  }
};

// Slide management
const handleSlideState = ( slide, isActive ) => {
  if ( isActive ) {
    forceLoadLazyImages( slide );
  }
  slide?.classList?.toggle( 'active', isActive );
};

// Set min-width on active slide's .e-div to prevent content juggle during expansion
const setContentMinWidth = ( slides, activeSlide ) => {
  const swiperWrapper = activeSlide.closest( '.swiper-wrapper' );
  if ( !swiperWrapper ) return;

  const gap = parseFloat( getComputedStyle( swiperWrapper ).columnGap ) || 0;
  const collapsedCount = slides.length - 1;
  const collapsedSlide = [ ...slides ].find( s => s !== activeSlide );
  const flexBasis = collapsedSlide
    ? parseFloat( getComputedStyle( collapsedSlide ).flexBasis ) || 250
    : 250;
  const activeWidth = swiperWrapper.offsetWidth - collapsedCount * ( flexBasis + gap );
  const minWidth = Math.max( 0, activeWidth - 60 );

  slides.forEach( slide => {
    const innerDiv = slide.querySelector( '.e-div--content-wrapper > .e-div:not(.e-div--collapsed-content)' );
    if ( !innerDiv ) return;
    innerDiv.style.minWidth = `${ minWidth }px`;
  } );
};

// Update all slides
const updateSlides = ( slides, activeSlide ) => {
  slides.forEach( slide => handleSlideState( slide, false ) );
  handleSlideState( activeSlide, true );
  setContentMinWidth( slides, activeSlide );
};

// Hover handler
const createHoverHandler = ( slides ) => {
  let isTransitioning = false;
  let currentHover = null;

  const handleHover = ( slide ) => {
    if ( isTransitioning || slide.classList.contains( 'active' ) ) return;

    isTransitioning = true;
    updateSlides( slides, slide );

    setTimeout( () => {
      isTransitioning = false;
      if ( currentHover && currentHover !== slide ) {
        updateSlides( slides, currentHover );
      }
    }, 800 );
  };

  return {
    handleHover,
    setCurrentHover: ( slide ) => { currentHover = slide; }
  };
};

// Check screen size
const shouldActivateForScreenSize = ( wrapper ) => {
  return (
    ( wrapper.classList.contains( 'e-stn--expanding-cards-with-testimonials-and-logo' ) ||
     wrapper.classList.contains( 'e-stn--expanding-cards-collapsed-with-text' ) ) &&
    window.matchMedia( '(min-width: 992px)' ).matches
  );
};

const cleanupWrapper = ( wrapper ) => {
  const slides = wrapper.querySelectorAll( '.swiper-slide' );
  slides.forEach( slide => {
    slide.classList.remove( 'active' );
    slide.querySelectorAll( '.e-clone' ).forEach( clone => clone.remove() );
    const innerDiv = slide.querySelector( '.e-div--content-wrapper > .e-div:not(.e-div--collapsed-content)' );
    if ( innerDiv ) innerDiv.style.minWidth = '';
    const collapsedWrap = slide.querySelector( '.e-div--collapsed-content' );
    if ( collapsedWrap ) {
      collapsedWrap.replaceWith( ...collapsedWrap.childNodes );
    }
  } );
};

// Resize handler
const processWrapperOnResize = ( wrapper ) => {
  if ( shouldActivateForScreenSize( wrapper ) ) {
    if ( !wrapper.dataset.initialized ) {
      setupWrapper( wrapper );
    } else {
      if ( wrapper.classList.contains( 'e-stn--expanding-cards-collapsed-with-text' ) ) {
        const slides = wrapper.querySelectorAll( '.swiper-slide' );
        slides.forEach( restructureSlideContent );
      }
      const slides = wrapper.querySelectorAll( '.swiper-slide' );
      const activeSlide = wrapper.querySelector( '.swiper-slide.active' );
      if ( activeSlide && slides.length ) {
        setContentMinWidth( slides, activeSlide );
      }
    }
  } else {
    cleanupWrapper( wrapper );
  }
};

const createResizeHandler = ( cardWrappers ) => {
  let timeoutId;
  return () => {
    clearTimeout( timeoutId );
    timeoutId = setTimeout( () => {
      cardWrappers.forEach( processWrapperOnResize );
    }, 250 );
  };
};

// Clone direct p and h3 of content-wrapper into its child .e-div in same order
const restructureSlideContent = ( slide ) => {
  const contentWrapper = slide.querySelector( '.e-div--content-wrapper' );
  if ( !contentWrapper ) return;

  const innerDiv = contentWrapper.querySelector( ':scope > .e-div' );
  if ( !innerDiv || innerDiv.querySelector( ':scope > .e-clone' ) ) return;

  const title = contentWrapper.querySelector( ':scope > .e-prg--title' );
  const heading = contentWrapper.querySelector( ':scope > h3' );

  if ( title && heading ) {
    const outerWrap = document.createElement( 'div' );
    outerWrap.classList.add( 'e-div--collapsed-content' );
    title.after( outerWrap );
    outerWrap.append( title, heading );
  }

  if ( heading ) {
    const headingClone = heading.cloneNode( true );
    headingClone.classList.add( 'e-clone' );
    innerDiv.prepend( headingClone );
  }
  if ( title ) {
    const titleClone = title.cloneNode( true );
    titleClone.classList.add( 'e-clone' );
    innerDiv.prepend( titleClone );
  }
};

// Setup
const setupWrapper = ( wrapper ) => {
  const slides = wrapper.querySelectorAll( '.swiper-slide' );
  if ( !slides?.length ) return;

  if ( wrapper.classList.contains( 'e-stn--expanding-cards-collapsed-with-text' ) ) {
    slides.forEach( restructureSlideContent );
  }

  if ( !wrapper.dataset.initialized ) {
    const hoverHandler = createHoverHandler( slides );

    slides.forEach( slide => {
      slide.addEventListener( 'mouseenter', () => {
        if ( !shouldActivateForScreenSize( wrapper ) ) return;
        hoverHandler.setCurrentHover( slide );
        hoverHandler.handleHover( slide );
      } );

      slide.addEventListener( 'mouseleave', () => {
        hoverHandler.setCurrentHover( null );
      } );
    } );

    wrapper.dataset.initialized = 'true';
  }

  if ( slides.length >= 5 ) {
    updateSlides( slides, slides[2] );
  } else {
    updateSlides( slides, slides[0] );
  }

  slides.forEach( forceLoadLazyImages );
};

// Init
document.addEventListener( 'DOMContentLoaded', () => {
  const cardWrappers = document.querySelectorAll(
    '.e-stn--expanding-cards-with-testimonials-and-logo, ' +
  '.e-stn--expanding-cards-collapsed-with-text'
  );
  if ( !cardWrappers?.length ) return;

  window.addEventListener( 'resize', createResizeHandler( cardWrappers ) );

  const activeWrappers = Array.from( cardWrappers ).filter( shouldActivateForScreenSize );
  if ( !activeWrappers.length ) return;

  try {
    activeWrappers.forEach( setupWrapper );
  } catch ( error ) {
    console.error( 'Error initializing expanding cards:', error );
  }
} );
